import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
} from "react";
import { format } from "date-fns";
import { Paperclip } from "lucide-react";
import { HELPERS } from "@/components/new-look/labels";
import { ATTACHMENT_ACCEPT, type JobAttachment } from "@/lib/api/mailbox";
import { cn } from "@/lib/utils";
import type { StreamItem, StreamWho } from "./idea-stream";
import { AttachmentTray, SentAttachments, useAttachmentTray } from "./stream-attachments";

/**
 * The one-stream window (docs/new-look/PLAN.md, steps 4.2–4.3): the
 * conversation and the build's activity in one place, newest at the bottom.
 * Terminal view by default, a Chat view for people who prefer bubbles, and
 * filters for everything, just the talk, or just the activity. A message box
 * at the bottom talks to whoever is working on the idea (4.4).
 */

/** The message box: who you're writing to, and what happens when you send. */
export interface StreamComposer {
  /** e.g. "Message the Builder". */
  placeholder: string;
  /** Sends the text (and any uploaded files); resolves when done. */
  onSend: (text: string, attachmentIds?: string[]) => Promise<void>;
  /**
   * Uploads one file for the next message (ER-133). Absent where files can't
   * go: a note to a running build, or a follow-up that has no run yet.
   */
  upload?: ((file: File) => Promise<JobAttachment>) | undefined;
  sending: boolean;
  error: string | null;
  /** Commands shown as a hint, e.g. ["/stop", "/status"]. */
  commands: readonly string[];
}

/** A question to confirm before doing something (e.g. /stop). */
export interface StreamConfirm {
  text: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export type StreamView = "terminal" | "chat";
export type StreamFilter = "all" | "talk" | "activity";

const VIEW_KEY = "compbox-stream-view";

function storedView(): StreamView {
  try {
    return localStorage.getItem(VIEW_KEY) === "chat" ? "chat" : "terminal";
  } catch {
    return "terminal";
  }
}

function rememberView(v: StreamView): void {
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    // Storage blocked: the view lasts until the page reloads.
  }
}

const WHO: Record<StreamWho, { label: string; terminal: string; chat: string }> = {
  you: { label: "You", terminal: "text-amber-300", chat: "text-blue-100" },
  ideaHelper: {
    label: HELPERS.ideaHelper,
    terminal: "text-violet-300",
    chat: "text-violet-700 dark:text-violet-300",
  },
  blueprintMaker: {
    label: HELPERS.blueprintMaker,
    terminal: "text-cyan-300",
    chat: "text-cyan-700 dark:text-cyan-300",
  },
  builder: {
    label: HELPERS.builder,
    terminal: "text-blue-300",
    chat: "text-blue-700 dark:text-blue-300",
  },
  compbox: { label: "CompBox", terminal: "text-slate-300", chat: "text-muted-foreground" },
};

const MARK: Record<StreamItem["tone"], { char: string; color: string }> = {
  plain: { char: "›", color: "text-blue-400" },
  ok: { char: "✓", color: "text-green-500" },
  error: { char: "!", color: "text-red-500" },
};

const FILTERS: { key: StreamFilter; label: string; short: string }[] = [
  { key: "all", label: "Everything", short: "Everything" },
  { key: "talk", label: "Just the conversation", short: "Talk" },
  { key: "activity", label: "Just activity", short: "Activity" },
];

function timeOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : format(d, "HH:mm");
}

function TerminalLine({ item }: { item: StreamItem }) {
  const who = WHO[item.who];
  const mark = MARK[item.tone];
  const isTalk = item.kind === "talk";
  return (
    <li data-kind={item.kind} className="flex gap-2 sm:gap-3">
      <time dateTime={item.at} className="w-10 shrink-0 text-slate-500 sm:w-12">
        {timeOf(item.at)}
      </time>
      {isTalk ? (
        <span className={cn("w-16 shrink-0 truncate font-bold sm:w-32", who.terminal)}>
          {who.label} ›
        </span>
      ) : (
        <span aria-hidden="true" className={cn("w-4 shrink-0 font-bold", mark.color)}>
          {mark.char}
        </span>
      )}
      <span
        className={cn(
          "min-w-0 whitespace-pre-wrap break-words",
          isTalk ? "text-slate-100" : "text-slate-400"
        )}
      >
        {item.text}
        <SentAttachments attachments={item.attachments} terminal />
      </span>
    </li>
  );
}

function ChatLine({ item }: { item: StreamItem }) {
  if (item.kind === "activity") {
    const mark = MARK[item.tone];
    return (
      <li
        data-kind={item.kind}
        className="flex justify-center gap-2 px-2 text-xs text-muted-foreground"
      >
        <span aria-hidden="true" className={mark.color}>
          {mark.char}
        </span>
        <span className="min-w-0 break-words">{item.text}</span>
        <time dateTime={item.at} className="shrink-0 opacity-70">
          {timeOf(item.at)}
        </time>
      </li>
    );
  }
  const mine = item.who === "you";
  const who = WHO[item.who];
  return (
    <li data-kind={item.kind} className={cn("flex", mine ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "flex max-w-[88%] flex-col gap-1 rounded-2xl px-3.5 py-2.5 sm:max-w-[76%]",
          mine
            ? "rounded-br-md bg-primary text-primary-foreground"
            : "rounded-bl-md border border-border bg-background text-foreground"
        )}
      >
        <span className={cn("text-xs font-bold", who.chat)}>
          {who.label} <span className="font-normal opacity-70">{timeOf(item.at)}</span>
        </span>
        {item.text && (
          <span className="whitespace-pre-wrap break-words text-[15px] leading-relaxed">
            {item.text}
          </span>
        )}
        <SentAttachments attachments={item.attachments} terminal={false} mine={mine} />
      </div>
    </li>
  );
}

export function IdeaStreamPanel({
  items,
  loading,
  live,
  title,
  composer,
  confirm,
  readOnly,
}: {
  items: readonly StreamItem[];
  loading: boolean;
  /** Something is running right now. */
  live: boolean;
  title: string;
  /** Absent when nobody is listening (no interview or run to talk to). */
  composer?: StreamComposer | undefined;
  confirm?: StreamConfirm | null | undefined;
  /** Shown instead of the message box when someone else owns the run. */
  readOnly?: string | undefined;
}) {
  const [draft, setDraft] = useState("");
  const files = useAttachmentTray(composer?.upload);
  const canAttach = !!composer?.upload;
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const text = draft.trim();
  // Files alone can be sent, but never while one is still uploading.
  const canSend =
    !!composer && !composer.sending && !files.uploading && (!!text || files.uploadedIds.length > 0);
  const send = (e: FormEvent) => {
    e.preventDefault();
    if (!composer || !canSend) return;
    const ids = files.uploadedIds;
    // Keep the text and files when sending fails, so they can be sent again.
    composer.onSend(text, ids.length > 0 ? ids : undefined).then(
      () => {
        setDraft("");
        files.clear();
      },
      () => undefined
    );
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (!canAttach) return;
    const pasted = Array.from(e.clipboardData.items)
      .filter((i) => i.kind === "file")
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null);
    if (pasted.length > 0) {
      e.preventDefault();
      files.add(pasted);
    }
  };
  const onDrop = (e: DragEvent<HTMLElement>) => {
    if (!canAttach) return;
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) files.add(dropped);
  };
  const [view, setView] = useState<StreamView>(storedView);
  const [filter, setFilter] = useState<StreamFilter>("all");
  const terminal = view === "terminal";
  const shown = filter === "all" ? items : items.filter((i) => i.kind === filter);

  const log = useRef<HTMLDivElement>(null);
  const count = shown.length;
  // Keep the newest line in view inside the window (never scrolls the page).
  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [count, view]);

  const chooseView = (v: StreamView) => {
    setView(v);
    rememberView(v);
  };

  return (
    <section
      aria-label="Conversation and activity"
      onDragOver={(e) => {
        if (!canAttach) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setDragging(false);
      }}
      onDrop={onDrop}
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border",
        dragging && "ring-2 ring-primary",
        terminal
          ? "border-slate-800 bg-[#0B1220] text-slate-200 shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
          : "border-border bg-muted/30"
      )}
    >
      <div
        className={cn(
          "flex items-center gap-3 px-3 py-2 text-xs sm:px-4",
          terminal
            ? "border-b border-slate-800 bg-[#111A2E] font-mono text-slate-400"
            : "border-b border-border bg-background text-muted-foreground"
        )}
      >
        {terminal && (
          <span aria-hidden="true" className="hidden gap-1.5 sm:flex">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
          </span>
        )}
        <span className="min-w-0 flex-1 truncate">
          {terminal ? title : "Conversation and activity"}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn(
              "h-2 w-2 rounded-full",
              live ? "animate-pulse bg-green-500" : "bg-slate-400"
            )}
          />
          {live ? "live" : "idle"}
        </span>
        <div
          role="group"
          aria-label="View"
          className={cn(
            "flex overflow-hidden rounded-lg border font-sans",
            terminal ? "border-slate-700" : "border-border"
          )}
        >
          {(["terminal", "chat"] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => {
                chooseView(v);
              }}
              className={cn(
                "min-h-9 px-3 text-xs font-semibold capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                view === v
                  ? terminal
                    ? "bg-slate-800 text-slate-100"
                    : "bg-accent text-accent-foreground"
                  : terminal
                    ? "text-slate-400 hover:text-slate-200"
                    : "text-muted-foreground hover:text-foreground"
              )}
            >
              {v === "terminal" ? "Terminal" : "Chat"}
            </button>
          ))}
        </div>
      </div>

      <div
        role="group"
        aria-label="Show"
        className={cn(
          "flex flex-wrap gap-1.5 px-3 py-2 font-sans sm:px-4",
          terminal ? "border-b border-slate-800/70" : "border-b border-border bg-background"
        )}
      >
        {FILTERS.map((f) => {
          const on = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              aria-pressed={on}
              aria-label={f.label}
              onClick={() => {
                setFilter(f.key);
              }}
              className={cn(
                "min-h-8 rounded-full border px-3 text-xs",
                on
                  ? terminal
                    ? "border-slate-200 bg-slate-100 text-slate-900"
                    : "border-foreground bg-foreground text-background"
                  : terminal
                    ? "border-slate-700 text-slate-400 hover:text-slate-200"
                    : "border-border text-foreground/80 hover:bg-muted"
              )}
            >
              <span aria-hidden="true" className="sm:hidden">
                {f.short}
              </span>
              <span aria-hidden="true" className="hidden sm:inline">
                {f.label}
              </span>
            </button>
          );
        })}
      </div>

      <div
        ref={log}
        role="log"
        aria-live="polite"
        className={cn(
          "h-[380px] overflow-y-auto px-3 py-3 sm:h-[460px] sm:px-4",
          terminal ? "font-mono text-[12.5px] leading-relaxed sm:text-[13px]" : "text-sm"
        )}
      >
        {loading && count === 0 ? (
          <p className={terminal ? "text-slate-500" : "text-muted-foreground"}>Loading…</p>
        ) : count === 0 ? (
          <p className={terminal ? "text-slate-500" : "text-muted-foreground"}>
            {items.length === 0
              ? "Nothing yet. Every message and every step shows up here as it happens."
              : "Nothing of this kind yet."}
          </p>
        ) : (
          <ul className={cn("flex flex-col", terminal ? "gap-1" : "gap-2.5")}>
            {shown.map((item) =>
              terminal ? (
                <TerminalLine key={item.id} item={item} />
              ) : (
                <ChatLine key={item.id} item={item} />
              )
            )}
          </ul>
        )}
      </div>

      {confirm && (
        <div
          role="alertdialog"
          aria-label="Confirm"
          className={cn(
            "flex flex-wrap items-center gap-2 px-3 py-2.5 font-sans text-sm sm:px-4",
            terminal
              ? "border-t border-amber-500/40 bg-amber-500/10 text-amber-100"
              : "border-t border-amber-300 bg-amber-50 text-amber-950"
          )}
        >
          <span className="min-w-0 flex-1">{confirm.text}</span>
          <button
            type="button"
            onClick={confirm.onCancel}
            className="min-h-9 rounded-lg px-3 text-sm font-medium hover:underline"
          >
            Keep going
          </button>
          <button
            type="button"
            onClick={confirm.onConfirm}
            className="min-h-9 rounded-lg bg-red-600 px-3 text-sm font-semibold text-white hover:bg-red-700"
          >
            {confirm.confirmLabel}
          </button>
        </div>
      )}

      {!composer && readOnly && (
        <p
          className={cn(
            "px-3 py-2.5 text-sm sm:px-4",
            terminal
              ? "border-t border-slate-800 font-mono text-slate-400"
              : "border-t border-border bg-background text-muted-foreground"
          )}
        >
          {readOnly}
        </p>
      )}

      {composer && (
        <form
          onSubmit={send}
          className={cn(
            "flex flex-col gap-1 px-3 py-2.5 sm:px-4",
            terminal ? "border-t border-slate-800" : "border-t border-border bg-background"
          )}
        >
          {dragging && (
            <p
              className={cn(
                "text-xs",
                terminal ? "font-mono text-slate-400" : "text-muted-foreground"
              )}
            >
              Drop files to attach them.
            </p>
          )}
          <AttachmentTray tray={files.tray} onRemove={files.remove} terminal={terminal} />
          <div className="flex items-center gap-2">
            {terminal && (
              <span aria-hidden="true" className="font-mono font-bold text-green-400">
                ❯
              </span>
            )}
            <label htmlFor="stream-input" className="sr-only">
              {composer.placeholder}
            </label>
            <input
              id="stream-input"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
              }}
              onPaste={onPaste}
              placeholder={
                terminal ? `${composer.placeholder}, or type a /command` : composer.placeholder
              }
              autoComplete="off"
              className={cn(
                "min-h-11 min-w-0 flex-1 text-base outline-none sm:text-sm",
                terminal
                  ? "border-0 bg-transparent font-mono text-slate-100 placeholder:text-slate-500"
                  : "rounded-xl border border-border bg-background px-3 text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
              )}
            />
            {canAttach && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  multiple
                  hidden
                  data-testid="stream-file-input"
                  onChange={(e) => {
                    files.add(Array.from(e.target.files ?? []));
                    // Reset so picking the same file again still works.
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  aria-label="Attach files"
                  title="Attach files: images, PDF, text, CSV, Markdown or JSON, up to 2 MB"
                  onClick={() => fileInput.current?.click()}
                  className={cn(
                    "inline-flex min-h-10 min-w-10 items-center justify-center rounded-lg",
                    terminal
                      ? "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <Paperclip aria-hidden="true" className="h-4 w-4" />
                </button>
              </>
            )}
            <button
              type="submit"
              aria-label="Send"
              disabled={!canSend}
              className={cn(
                "min-h-10 min-w-11 rounded-lg px-3 text-sm font-semibold disabled:opacity-50",
                terminal
                  ? "border border-slate-700 font-mono text-slate-200 hover:bg-slate-800"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              )}
            >
              <span aria-hidden="true">{composer.sending ? "…" : terminal ? "↵" : "Send"}</span>
            </button>
          </div>
          {files.error && (
            <p
              role="alert"
              className={cn("text-xs", terminal ? "text-red-300" : "text-destructive")}
            >
              {files.error}
            </p>
          )}
          {composer.error && (
            <p
              role="alert"
              className={cn("text-xs", terminal ? "text-red-300" : "text-destructive")}
            >
              {composer.error}
            </p>
          )}
          {composer.commands.length > 0 && (
            <p
              className={cn(
                "hidden text-[11.5px] sm:block",
                terminal ? "font-mono text-slate-500" : "text-muted-foreground"
              )}
            >
              {[...composer.commands, "or just type to talk"].join(" · ")}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
